// ShipmentsPage — /receiving/shipments. Every packing list that has been read.
//
// ── WHO OPENS THIS, AND WHY THAT DECIDED THE LAYOUT ─────────────────────────
// Nobody browses shipment history for pleasure. This page has exactly one
// visitor: somebody on the phone to a vendor about a delivery, six weeks after it
// landed, holding a slip number or a purchase order number and nothing else.
//
// So the search box takes all three strings they might have (slip number, supplier,
// purchase order), it is the first thing under the heading, and it is focused
// nowhere — because the other half of the traffic is "what came in this week", which
// the default newest-first list answers without typing anything.
//
// ── WHY THE ROUTE IS /shipments AND NOT /packing-lists ──────────────────────
// The reader lives at /receiving/packing-list, singular. A history at
// /receiving/packing-lists would differ from it by ONE CHARACTER, and a typo in a
// bookmark or a pasted link would land on the wrong screen silently. "Shipment" is
// also the honest noun for the record: the table is packing_lists, but what it
// records is a delivery. The copy still says "packing list" everywhere, so the
// connection is never in doubt.

import React from 'react';
import { buttonClass } from '../components/ui/Button';
import { Link, Redirect } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import { FilterBar, FilterSearch } from '../components/FilterBar';
import { DataTable } from '../components/table/DataTable';
import { formatEventDate } from '../utils/date-utils';
import { usePackingListApi, type PackingListSummary } from './packingListApi';
import PageLayout from '../components/ui/PageLayout';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';

/**
 * The wait, at the rhythm of the rows it stands in for.
 *
 * A per-column skeleton cannot exist here: the shared table renders one
 * full-width loading row, so there are no columns to lay bars across. Stacked
 * bars at the row pitch keep the table from jumping when data lands, which is
 * the whole job.
 */
const LoadingBars: React.FC = () => (
  <SkeletonGroup label="Loading shipments" className="px-4 py-8 space-y-3">
    {['w-3/5', 'w-2/5', 'w-3/4', 'w-1/3'].map((width) => (
      <Skeleton key={width} className={`h-3 ${width}`} />
    ))}
  </SkeletonGroup>
);

/**
 * How much of the shipment is on a shelf.
 *
 * THREE STATES, not a percentage. "5 of 7" is a number somebody has to interpret;
 * the three words below are the answer to the only question being asked, which is
 * whether this delivery is finished with.
 */
function progressOf(row: PackingListSummary): { label: string; className: string } {
  if (row.line_count === 0) return { label: 'no lines', className: 'text-on-surface-variant' };
  if (row.lines_boxed >= row.line_count) {
    return { label: 'all received', className: 'text-green-300' };
  }
  if (row.lines_boxed === 0) {
    return { label: 'none received yet', className: 'text-amber-300' };
  }
  return {
    label: `${row.lines_boxed} of ${row.line_count} received`,
    className: 'text-amber-300',
  };
}

// Module-scope on purpose (the DataTable contract): every cell is pure over its
// row and nothing here closes over page state. No `sortable` — the server hands
// these back newest-first, which is the order somebody scanning "what came in
// this week" wants, and a re-sortable date column would let them lose it.
const SHIPMENT_COLUMNS: ColumnDef<PackingListSummary, any>[] = [
  {
    id: 'ship_date',
    header: 'Shipped',
    meta: { numeric: true, cellClass: 'whitespace-nowrap text-on-surface' },
    cell: ({ row }) =>
      row.original.ship_date ? formatEventDate(row.original.ship_date, 'MMM D, YYYY') : '—',
  },
  {
    id: 'slip_number',
    header: 'Slip',
    meta: { cellClass: 'font-mono text-primary' },
    cell: ({ row }) =>
      row.original.slip_number || <span className="text-on-surface-variant italic font-sans">unnumbered</span>,
  },
  {
    id: 'supplier',
    header: 'Supplier',
    meta: { cellClass: 'text-on-surface' },
    // The resolved vendor when there is one, the printed name otherwise. They
    // differ more often than anyone expects.
    cell: ({ row }) => row.original.vendor_name || row.original.vendor_observed || '—',
  },
  {
    id: 'po_numbers',
    header: 'Against',
    cell: ({ row }) =>
      row.original.po_numbers.length === 0 ? (
        <span className="text-xs text-on-surface-variant italic">no order</span>
      ) : (
        <span className="flex flex-wrap gap-1">
          {row.original.po_numbers.map((po) => (
            <span key={po} className="font-mono text-[11px] text-on-surface">
              {po}
            </span>
          ))}
        </span>
      ),
  },
  {
    id: 'progress',
    header: 'Booked in',
    meta: { numeric: true, cellClass: 'text-xs' },
    // The color is per ROW, and meta.cellClass is per COLUMN, so it rides on a
    // span inside the cell rather than on the td.
    cell: ({ row }) => {
      const progress = progressOf(row.original);
      return <span className={progress.className}>{progress.label}</span>;
    },
  },
  {
    id: 'view',
    // No label: the column is the way in, and "View" said twice is furniture.
    header: '',
    meta: { align: 'right' },
    cell: ({ row }) => (
      <Link
        to={`/receiving/shipments/${row.original.id}`}
        className="text-xs text-primary hover:underline"
      >
        View →
      </Link>
    ),
  },
];

export const ShipmentsPage: React.FC = () => {
  const api = usePackingListApi();
  const toast = useToast();
  const user = useUserStore((s) => s.user);

  const canAccess =
    user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement;

  const [rows, setRows] = React.useState<PackingListSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');

  const fetchRows = React.useCallback(async (q: string) => {
    setLoading(true);
    try {
      const data = await api.list({ q: q.trim() || undefined, limit: 200 });
      setRows(data.rows || []);
    } catch (err: any) {
      toast.error(err?.data?.error?.message || err?.message || 'Failed to load shipments');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced, like the receiving hub's own search. 250ms is that page's number
  // and this one should not feel different.
  React.useEffect(() => {
    const t = setTimeout(() => fetchRows(search), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchRows, search]);

  if (!canAccess) return <Redirect to="/" />;

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4" header={(
      <PageHeader
        className="mb-5"
        title="Shipments"
        helpTitle="About shipments"
        lede="Every packing list that has been read, newest first."
        help={
          <>
            <p>
              One row per delivery: what the supplier said they sent, which purchase orders it
              was against, and how much of it has actually been booked in.
            </p>
            <p className="text-on-surface font-medium">What this is for</p>
            <p>
              A supplier phones about a delivery weeks later. Search the slip number off their
              paperwork, the supplier name, or the purchase order number, and open the row:
              you get what they claimed to send, what the order expected, and every box that
              came in against it with its barcode.
            </p>
            <p className="text-on-surface font-medium">Received, and not received</p>
            <p>
              A shipment reads as fully received once every line has all its boxes booked in.
              Until then it says how many lines are done. That is worked out from the boxes
              themselves rather than stored, so it cannot drift from what is on the shelf.
            </p>
            <p className="text-on-surface font-medium">Rejected units still count as sent</p>
            <p>
              If some of a delivery was not acceptable, those units still appear here: the
              supplier did ship them. They show as a box at QC Hold rather than in the
              warehouse, and getting money back for them is tracked separately under Returns.
            </p>
          </>
        }
        primary={
          /* The page's one consequential act: reading a slip is what puts a row
             here in the first place. */
          <PrimaryAction to="/receiving/packing-list">Read a packing list</PrimaryAction>
        }
      />
    )}>
    <div className="space-y-5">
      <FilterBar>
        {/* The label, not the placeholder, is what a reader gets once there is
            text in the box: it names all three strings the caller might hold. */}
        <FilterSearch
          value={search}
          onChange={setSearch}
          label="Search shipments by slip number, supplier, or purchase order"
          placeholder="Search slip number, supplier, PO…"
          className="flex-1 min-w-[220px] max-w-sm"
        />
      </FilterBar>

      {/* The vessel is the shared table's: shell, radius, scroll container, and
          the face on every row. The count moved into the strip, where it sits
          against the pile it counts instead of floating above it, and it keeps
          its live region so a search is still announced. */}
      <DataTable<PackingListSummary>
        data={rows}
        columns={SHIPMENT_COLUMNS}
        getRowId={(r) => r.id}
        strip={
          <div className="ml-auto text-xs text-on-surface-variant tabular-nums" role="status" aria-live="polite">
            {rows.length} shipment{rows.length === 1 ? '' : 's'}
          </div>
        }
        loading={loading}
        loadingContent={<LoadingBars />}
        empty={
          /* Two empty states wearing one row. "Nothing has been read yet"
             and "your search matched nothing" are opposite problems, and
             only the first one needs teaching. */
          search.trim() ? (
            <EmptyState
              variant="area"
              action={(
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className={buttonClass('tonal', 'md')}
                >
                  Clear the search
                </button>
              )}
            >
              Nothing matches that.
            </EmptyState>
          ) : (
            <EmptyState
              variant="area"
              title="No packing lists have been read yet."
              action={(
                <Link to="/receiving/packing-list" className={buttonClass('filled', 'md')}>
                  Read one now
                </Link>
              )}
            >
              When a box arrives with a slip taped to it, read it first and the
              receiving list fills itself in. Every one you read shows up here as
              the record of that delivery.
            </EmptyState>
          )
        }
      />
    </div>
    </PageLayout>
  );
};

export default ShipmentsPage;
